<template>
    <div class="table-row summary-row" v-if="records && records.length > 0">
        <!-- Sol taraftaki özet başlık hücresi -->
        <div class="table-cell summary-label-cell">Summary</div>

        <!-- Her field için özet hücresi -->
        <div class="table-cell" v-for="(field, index) in visibleFields" :key="field._id">
            <!-- Dropdown alan özeti -->
            <template v-if="field.fieldType === 'dropdown'">
                <div class="dropdown-summary">
                    <div
                        v-for="dist in getDropdownDistribution(records, field)"
                        :key="dist.value"
                        class="summary-option-chip"
                        :style="{
                            backgroundColor: dist.color,
                            flexGrow: dist.flexValue,
                        }"
                        v-tippy="{ content: getDropdownTooltip(dist) }"
                        @mouseover="hoverOption(dist)"
                        @mouseleave="leaveOption(dist)"
                    >
                        <!-- Renk barı olarak boş içerik -->
                    </div>
                </div>
            </template>

            <!-- Sayısal alanlar için sum + tooltip -->
            <template v-else-if="field.fieldType === 'number' || field.fieldType === 'currency'">
                <span class="numeric-summary" v-tippy="{ content: getNumericTooltip(records, field) }">
                    Sum: {{ getNumericSum(records, field) }}
                </span>
            </template>

            <!-- Tarihsel alanlar için min - max format + tooltip -->
            <template v-else-if="field.fieldType === 'datetime'">
                <span class="date-summary-chip" v-tippy="{ content: getDateRangeTooltip(records, field) }">
                    {{ getShortDateRange(records, field) }}
                </span>
            </template>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";

export default Vue.extend({
    name: "TableFooterSummary",
    props: {
        records: {
            type: Array,
            required: true,
        },
        visibleFields: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            hoveredValue: null,
        };
    },
    methods: {
        //---------------------------------------
        // 1) Dropdown Summaries
        //---------------------------------------
        getDropdownDistribution(records, field) {
            var distribution = {};
            var totalCount = 0;
            for (var i = 0; i < records.length; i++) {
                var record = records[i];
                var value = record.data[field._id];
                if (Array.isArray(value)) {
                    for (var j = 0; j < value.length; j++) {
                        var multiVal = value[j];
                        if (!distribution[multiVal]) {
                            distribution[multiVal] = 0;
                        }
                        distribution[multiVal]++;
                        totalCount++;
                    }
                } else {
                    if (!value) continue;
                    if (!distribution[value]) {
                        distribution[value] = 0;
                    }
                    distribution[value]++;
                    totalCount++;
                }
            }

            // Statik modda seçenek renklerini alabilmek için
            var options = [];
            if (field.options && field.options.dropdown && field.options.dropdown.mode === "static") {
                options = field.options.dropdown.options;
            }

            var resultArray = [];
            for (var key in distribution) {
                if (distribution.hasOwnProperty(key)) {
                    var count = distribution[key];
                    var colorVal = this.findOptionColor(key, options);
                    if (!colorVal) {
                        colorVal = this.generateColorFromText(key);
                    }
                    var ratio = count / totalCount;
                    resultArray.push({
                        value: key,
                        count: count,
                        color: colorVal,
                        flexValue: ratio * 10,
                        ratio: ratio,
                    });
                }
            }

            // Büyükten küçüğe sırala
            resultArray.sort(function (a, b) {
                return b.count - a.count;
            });

            return resultArray;
        },
        findOptionColor(value, options) {
            if (!options || !options.length) {
                return null;
            }
            for (var i = 0; i < options.length; i++) {
                if (options[i].value === value) {
                    return options[i].color;
                }
            }
            return null;
        },
        generateColorFromText(text) {
            var hash = 0;
            for (var i = 0; i < text.length; i++) {
                hash = text.charCodeAt(i) + ((hash << 5) - hash);
            }
            var hue = Math.abs(hash) % 360;
            var saturation = 25 + (Math.abs(hash) % 15);
            var lightness = 85 + (Math.abs(hash) % 10);
            return "hsl(" + hue + ", " + saturation + "%, " + lightness + "%)";
        },

        // Dropdown tooltip içeriği
        getDropdownTooltip(dist) {
            // Yüzdeyi hesaplayıp metne ekleyelim
            var percentage = (dist.ratio * 100).toFixed(2);
            return dist.value + " | Count: " + dist.count + " | Percentage: " + percentage + "%";
        },

        hoverOption(dist) {
            this.hoveredValue = dist.value;
        },
        leaveOption(dist) {
            this.hoveredValue = null;
        },

        //---------------------------------------
        // 2) Numeric Summaries
        //---------------------------------------
        getNumericSum(records, field) {
            var sum = 0;
            var validCount = 0;
            for (var i = 0; i < records.length; i++) {
                var val = records[i].data[field._id];
                if (val || val === 0) {
                    var numericVal = parseFloat(val);
                    if (!isNaN(numericVal)) {
                        sum += numericVal;
                        validCount++;
                    }
                }
            }
            return sum;
        },
        getNumericTooltip(records, field) {
            // Sum, avg, min, max, count hesaplayalım
            var sum = 0;
            var count = 0;
            var minVal = null;
            var maxVal = null;

            for (var i = 0; i < records.length; i++) {
                var val = records[i].data[field._id];
                if (val || val === 0) {
                    var numericVal = parseFloat(val);
                    if (!isNaN(numericVal)) {
                        sum += numericVal;
                        count++;
                        if (minVal === null || numericVal < minVal) {
                            minVal = numericVal;
                        }
                        if (maxVal === null || numericVal > maxVal) {
                            maxVal = numericVal;
                        }
                    }
                }
            }
            var avg = count > 0 ? (sum / count).toFixed(2) : "-";

            return (
                "Sum: " +
                sum +
                " | Avg: " +
                avg +
                " | Min: " +
                (minVal !== null ? minVal : "-") +
                " | Max: " +
                (maxVal !== null ? maxVal : "-") +
                " | Count: " +
                count
            );
        },

        //---------------------------------------
        // 3) Date Summaries
        //---------------------------------------
        getShortDateRange(records, field) {
            var minDate = null;
            var maxDate = null;

            for (var i = 0; i < records.length; i++) {
                var val = records[i].data[field._id];
                if (val) {
                    var dateObj = moment(val);
                    if (dateObj.isValid()) {
                        if (!minDate || dateObj.isBefore(minDate)) {
                            minDate = dateObj;
                        }
                        if (!maxDate || dateObj.isAfter(maxDate)) {
                            maxDate = dateObj;
                        }
                    }
                }
            }
            if (minDate && maxDate) {
                return minDate.format("MMM DD") + " - " + maxDate.format("MMM DD");
            }
            return "";
        },

        // Tarihsel alanlar için tooltip içeriği
        getDateRangeTooltip(records, field) {
            var range = this.calculateDateRange(records, field);
            if (!range) {
                return "Date data not found";
            }
            var dayDiff = range.maxDate.diff(range.minDate, "days");
            if (dayDiff === 0) {
                return "Same day";
            } else if (dayDiff === 1) {
                return "1 day interval";
            } else {
                return dayDiff + " day interval";
            }
        },

        // Min - max tarihleri hesaplayan yardımcı metod
        calculateDateRange(records, field) {
            var minDate = null;
            var maxDate = null;

            for (var i = 0; i < records.length; i++) {
                var val = records[i].data[field._id];
                if (val) {
                    var dateObj = moment(val);
                    if (dateObj.isValid()) {
                        if (!minDate || dateObj.isBefore(minDate)) {
                            minDate = dateObj;
                        }
                        if (!maxDate || dateObj.isAfter(maxDate)) {
                            maxDate = dateObj;
                        }
                    }
                }
            }

            if (!minDate || !maxDate) {
                return null;
            }
            return { minDate: minDate, maxDate: maxDate };
        },
    },
});
</script>

<style scoped lang="scss">
.summary-row {
    background-color: #f3f3f3;
    font-weight: bold;

    .summary-label-cell {
        justify-content: center;
        font-weight: bold;
    }
}

.dropdown-summary {
    width: 100%;
    height: 18px;
    display: flex;
    align-items: center;
    border: 1px solid #ddd;
    border-radius: 4px;

    .summary-option-chip {
        height: 100%;
        transition: transform 0.2s ease;
        cursor: pointer;

        &:hover {
            transform: scale(1.2);
            height: 120%;
        }
    }
}

.numeric-summary {
    color: #333;
}

.date-summary-chip {
    display: inline-block;
    padding: 2px 6px;
    background-color: #d9f7be;
    border-radius: 4px;
    cursor: default;
}

.table-cell {
    &.summary-label-cell {
        min-width: 120px;
    }
}
</style>
